import { useCallback, useEffect, useRef } from 'react';
import { useSplitterDrag } from '../../../../layout/hooks/useSplitterDrag';

const ROW_SIDE_PANEL_MIN_WIDTH = 240;
const ROW_SIDE_PANEL_MAX_WIDTH = 800;
const ROW_SIDE_PANEL_DIVIDER_WIDTH = 8;
const GRID_MIN_WIDTH_WITH_SIDE_PANEL = 240;

/** Keyboard resize step for the row side panel, in pixels (Shift = 4x). */
const ROW_SIDE_PANEL_KEY_STEP = 8;
const ROW_SIDE_PANEL_KEY_STEP_BIG = 32;

export { ROW_SIDE_PANEL_MIN_WIDTH, ROW_SIDE_PANEL_MAX_WIDTH, ROW_SIDE_PANEL_DIVIDER_WIDTH, GRID_MIN_WIDTH_WITH_SIDE_PANEL };

type UseRowSidePanelResizeParams = {
  isOpen: boolean;
  viewMode: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  widthPx: number;
  setWidthPx: React.Dispatch<React.SetStateAction<number>>;
  /** Real default the caller initialised `widthPx` to — double-click resets here. */
  resetWidthPx: number;
  /** Called after a double-click reset (e.g. to fire the `feature:splitter_reset` analytics event). */
  onReset?: () => void;
};

/**
 * Row side panel divider (CH-13 site 7 — the row-side-panel divider inside
 * `ResultsGrid.tsx`, also the structural twin of `TableExplorerTab.tsx`'s
 * row-details "side" divider). Wraps the shared `useSplitterDrag` hook:
 * pointer-capture + rAF drag, keyboard step, double-click reset. The width
 * math is unchanged from the pre-existing mouse-drag implementation
 * (position-based: the panel's width is the distance from the pointer's
 * absolute clientX to the container's right edge) — only the event
 * plumbing moved from `document` mousemove/mouseup to pointer capture, by
 * reconstructing the absolute clientX from the hook's delta.
 */
export function useRowSidePanelResize({
  isOpen,
  viewMode,
  containerRef,
  widthPx,
  setWidthPx,
  resetWidthPx,
  onReset,
}: UseRowSidePanelResizeParams) {
  const dragStartClientXRef = useRef(0);

  const widthForClientX = useCallback(
    (clientX: number): number => {
      const container = containerRef.current;
      if (!container) return widthPx;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      if (containerWidth <= 0) return widthPx;
      const relativeX = clientX - containerRect.left;
      const maxPanelWidth = Math.max(
        ROW_SIDE_PANEL_MIN_WIDTH,
        Math.min(ROW_SIDE_PANEL_MAX_WIDTH, containerWidth - GRID_MIN_WIDTH_WITH_SIDE_PANEL - ROW_SIDE_PANEL_DIVIDER_WIDTH),
      );
      const newPanelWidth = containerWidth - relativeX - ROW_SIDE_PANEL_DIVIDER_WIDTH / 2;
      return Math.round(Math.max(ROW_SIDE_PANEL_MIN_WIDTH, Math.min(maxPanelWidth, newPanelWidth)));
    },
    [containerRef, widthPx],
  );

  const splitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      setWidthPx(widthForClientX(dragStartClientXRef.current + deltaPx));
    },
    onReset: () => {
      setWidthPx(resetWidthPx);
      onReset?.();
    },
    onKeyStep: (direction, big) => {
      const step = big ? ROW_SIDE_PANEL_KEY_STEP_BIG : ROW_SIDE_PANEL_KEY_STEP;
      const container = containerRef.current;
      const maxPanelWidth = container
        ? Math.max(
            ROW_SIDE_PANEL_MIN_WIDTH,
            Math.min(ROW_SIDE_PANEL_MAX_WIDTH, container.getBoundingClientRect().width - GRID_MIN_WIDTH_WITH_SIDE_PANEL - ROW_SIDE_PANEL_DIVIDER_WIDTH),
          )
        : widthPx;
      setWidthPx(Math.max(ROW_SIDE_PANEL_MIN_WIDTH, Math.min(maxPanelWidth, widthPx + direction * step)));
    },
  });

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragStartClientXRef.current = e.clientX;
      splitter.onPointerDown(e);
    },
    [splitter],
  );

  useEffect(() => {
    if (!isOpen || viewMode === 'stack') return;
    const container = containerRef.current;
    if (!container) return;
    const containerWidth = container.getBoundingClientRect().width;
    if (containerWidth <= 0) return;
    const maxPanelWidth = Math.max(
      ROW_SIDE_PANEL_MIN_WIDTH,
      Math.min(ROW_SIDE_PANEL_MAX_WIDTH, containerWidth - GRID_MIN_WIDTH_WITH_SIDE_PANEL - ROW_SIDE_PANEL_DIVIDER_WIDTH),
    );
    setWidthPx((prev: number) => (prev > maxPanelWidth ? Math.round(maxPanelWidth) : prev));
  }, [isOpen, viewMode, containerRef, setWidthPx]);

  return {
    onPointerDown,
    onDoubleClick: splitter.onDoubleClick,
    onKeyDown: splitter.onKeyDown,
    isDragging: splitter.isDragging,
  };
}
